/*
 * AppReducer
 *
 * The reducer takes care of our data. Using actions, we can change our
 * application state.
 * To add a new action, add it to the switch statement in the reducer function
 *
 * Example:
 * case YOUR_ACTION_CONSTANT:
 *   return state.set('yourStateVariable', true);
 */

import { fromJS } from 'immutable';
import { combineReducers } from 'redux';

import clone from 'clone-deep';
import deepEqual from 'deep-equal';
import blank from 'spiceutils/dist/blank';

import undoable from 'utils/redux-undo';
import { pushHistoryState } from 'utils/history';

import {
  UPDATE_CDF,
  SET_PPTX_CDF,

  SET_CONTROL_LIST,
  STORE_TEMPORARY_QUSTION_CONTROL_LIST,
  SET_PACKAGE,
  SET_ROLE,
  SET_USER,
  SET_ORG,
  UPDATE_QUESTION_SUGGESTIONS,
  STORE_QUESTION_CONTROL_LIST,
  STORE_META_JSON,
  SET_META_DATA,

  UPDATE_CLUSTER_LIST,
  STORE_PRESET_CLUSTER_LISTS,
  SET_PRESET_CLUSTER_CONTROL,
  PPTX_DOWNLOAD_START,
  PPTX_DOWNLOAD_STOP,
  PPTX_DOWNLOAD_READY,
  PPTX_DOWNLOAD_FAILED,
  DOWNLOAD_NOTIFICATION_CLOSED,
  CSV_DOWNLOAD_READY,

  SET_SHOW_NEW_QUESTION,
  STORE_NEW_CHART,
  CHART_LOADING,
  CHART_ERROR,
  SET_PAGE_LOADING,
} from './constants';

// The initial state of the App
const initialMainState = fromJS({
  clientPackage: 'Global',
  questionSuggestions: [],
  questionControlList: {},
  pptxDownloadActive: false,
  pptxDownloadUrl: null,
  pptxDownloadFailed: false,
  pptxDownloadFailedStatus: null,
  csvDownloadUrl: null,
  presetBreakLists: [],
  presetCountryLists: [],
  tempControlList: {},
  role: null,
  currentUser: 'Unknown',
  metaJSON: {},
  showNewQuestionSelector: false,
  pageLoading: true,
  pptxCdf: null,
});

export const initialCdfState = fromJS({
  cdf: {},
  controlList: {},
  presetBreakList: 'None',
  presetCountryList: 'None',
  metaData: {},
  respondToBreakGroupChange: false,
});

export function cdfReducer(state = initialCdfState, action) {
  let newState;
  switch (action.type) {
    case UPDATE_CDF:
      pushNewHistoryState();
      if (action.field instanceof Array) {
        let i;
        newState = state;
        for (i = 0; i < action.field.length; i++) {
          if (action.field[i].value !== null) {
            newState = newState.setIn(['cdf', action.field[i].field], fromJS(action.field[i].value));
          } else if (action.field[i].value === null) {
            newState = newState.deleteIn(['cdf', action.field[i].field]);
          }
        }
        return newState;
      }
      if (action.field == null) {
        return state.set('cdf', fromJS(action.value));
      }
      if (action.value !== null) {
        return state.setIn(['cdf', action.field], fromJS(action.value));
      }
      return state.deleteIn(['cdf', action.field]);

    case SET_META_DATA: {
      let { questionCode } = action;
      if (!questionCode) {
        questionCode = state.getIn(['cdf', 'QuestionCode']);
      }
      return state.set('metaData', fromJS({ [questionCode]: action.metaData }));
    }

    // eslint-disable-next-line no-case-declarations
    case UPDATE_CLUSTER_LIST: {
      pushNewHistoryState();
      // Manually create the ClusterList entry before setting, to prevent weird behaviour or errors from setIn().
      let tempClusterList = state.getIn(['cdf', action.field]);
      if (tempClusterList === undefined) {
        return state.setIn(['cdf', action.field], fromJS([action.value]));
      }
      if (!(tempClusterList instanceof Array))tempClusterList = tempClusterList.toJS();

      // Create a clone of the existing ClusterList as if we use the same array object then react will not register a change in the state
      // This may be unnecesary now but further manual testing is required
      const newClusterList = clone(tempClusterList);
      newClusterList[action.index] = action.value;

      return state.setIn(['cdf', action.field], fromJS(newClusterList));
    }

    case SET_CONTROL_LIST:
      return state.set('controlList', fromJS(action.value));

    case STORE_NEW_CHART:
      pushNewHistoryState();
      newState = state.set('controlList', fromJS(action.controlList));
      newState = newState.set('respondToBreakGroupChange', !!action.respondToBreakGroupChange);
      newState = newState.set('pptxCdf', null);
      return newState.set('cdf', fromJS(action.cdf));

    case SET_PPTX_CDF:
      return state.set('pptxCdf', fromJS(action.value));

    case SET_PRESET_CLUSTER_CONTROL:
      newState = state.set(`preset${action.field}`, action.value);
      // action.list is the list that will appear in the CDF
      switch (action.list) {
        // null: delete list
        case null:
          pushNewHistoryState();
          return newState.deleteIn(['cdf', action.field]);
        // undefined: do not update list
        case undefined:
          return newState;
        // other: use given value
        default:
          pushNewHistoryState();
          return newState.setIn(['cdf', action.field], action.list);
      }

    case SET_SHOW_NEW_QUESTION: {
      // If not on advanced view
      if (!/^\/?advanced\/?/.test(window.location.pathname)) {
        // If action should result in visible question selector
        if (action.value === true) {
          // Invalidate CDF
          return state.set('cdf', fromJS({}));
        }
      }
      // Perform no action
      return state;
    }

    default:
      return state;
  }
}

export function appReducer(state = initialMainState, action) {
  let newState;
  switch (action.type) {
    case SET_CONTROL_LIST:
      return state.set('controlList', fromJS(action.value));

    case STORE_TEMPORARY_QUSTION_CONTROL_LIST:
      return state.set('tempControlList', fromJS(action.value));

    case SET_PACKAGE:
      return state.set('clientPackage', fromJS(action.value));

    case SET_USER:
      return state.set('currentUser', fromJS(action.value));

    case SET_ORG:
      return state.set('currentOrg', fromJS(action.value));

    case SET_ROLE:
      return state.set('role', action.value);

    case UPDATE_QUESTION_SUGGESTIONS:
      return state.set('questionSuggestions', fromJS(action.value));

    case STORE_QUESTION_CONTROL_LIST:
      return state.set('questionControlList', fromJS(action.value));

    case STORE_META_JSON:
      return state.set('metaJSON', fromJS(action.value));

    case STORE_PRESET_CLUSTER_LISTS:
      if (action.list) {
        return state.set(`preset${action.field}s`, fromJS(action.list));
      }
      return state.set(`preset${action.field}s`, fromJS([]));

    case PPTX_DOWNLOAD_START:
      newState = state.set('pptxDownloadUrl', null);
      newState = newState.set('pptxDownloadFailed', false);
      newState = newState.set('pptxDownloadFailedStatus', null);
      return newState.set('pptxDownloadActive', true);

    case PPTX_DOWNLOAD_STOP:
      return state.set('pptxDownloadActive', false);

    case PPTX_DOWNLOAD_FAILED:
      newState = state.set('pptxDownloadActive', false);
      newState = state.set('pptxDownloadFailedStatus', action.status);
      return newState.set('pptxDownloadFailed', true);

    case PPTX_DOWNLOAD_READY:
      newState = state.set('pptxDownloadActive', false);
      return newState.set('pptxDownloadUrl', action.pptxUrl);

    case DOWNLOAD_NOTIFICATION_CLOSED:
      newState = state.set('pptxDownloadActive', false);
      newState = newState.set('pptxDownloadFailed', false);
      newState = newState.set('csvDownloadUrl', null);
      return newState.set('pptxDownloadUrl', null);

    case CSV_DOWNLOAD_READY:
      return state.set('csvDownloadUrl', { url: action.value, code: action.questionCode });

    case SET_SHOW_NEW_QUESTION: {
      // If on advanced view
      if (/^\/?advanced\/?/.test(window.location.pathname)) {
        return state.set('showNewQuestionSelector', action.value);
      }
      return state;
    }
    case CHART_LOADING:
      newState = state.set('chartError', null);
      return newState.set('chartLoading', action.value);

    case CHART_ERROR:
      newState = state.set('chartLoading', false);
      return newState.set('chartError', action.value);

    case SET_PAGE_LOADING:
      return state.set('pageLoading', action.value);

    default:
      return state;
  }
}

const pushNewHistoryState = () => {
  setTimeout(pushHistoryState);
};

export default combineReducers({
  cdf: undoable(cdfReducer, {
    ignoreInitialState: true,
    filter: (action, currentState, history) => {
      const currentCdf = currentState.get('cdf')?.toJS();
      if (blank(currentCdf)) {
        return false;
      }

      const lastState = history.present;
      const lastCdf = lastState.get('cdf')?.toJS();
      return !deepEqual(lastCdf, currentCdf);
    } }),
  main: appReducer,
});



// WEBPACK FOOTER //
// ./app/containers/App/reducer.js